// Commander (v0.8.9): single-page operational view of who's
// working in the team right now. The Captain/leaderboard-style
// snapshot of recent moves, ticket closures, and per-user
// counters for a chosen window. Phase 1: counters + recent
// moves only. Phase 2 will add boss-fight chips and side-
// quest tally.
//
// The page is admin-only and lives at /admin/commander. It's
// not on the player top-nav — Commander is an operator view,
// not a player dashboard.

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { partitionContributors } from './commander-groups';

interface Team {
  id: string;
  name: string;
  slug: string;
}
interface Contributor {
  user_id: string;
  handle: string;
  name: string | null;
  color: string | null;
  last_seen_at: string | null;
  counters: {
    events: number;
    tickets_closed: number;
    prs_opened: number;
    prs_reviewed: number;
    prs_merged: number;
    other: number;
  };
  recent: Array<{
    event_id: string;
    type: string;
    occurred_at: string;
    ingestor_type: string;
    ingestor_name: string;
    target_external: string | null;
    description: string;
  }>;
  // v0.8.9 (Phase 2): per-contributor boss-fight involvement
  // and side-quest tally in the window.
  boss_fights: Array<{ id: string; name: string; damage: number; hp: number }>;
  side_quests: number;
}
interface CommanderSnapshot {
  team_id: string;
  window: string;
  window_hours: number;
  contributors: Contributor[];
  totals: {
    events: number;
    tickets_closed: number;
    prs_opened: number;
    prs_merged: number;
  };
}

// Window choices are a label (matches the api side).
const WINDOW_OPTIONS = [
  { id: '1h', label: 'Last 1 hour' },
  { id: '8h', label: 'Last 8 hours' },
  { id: '24h', label: 'Last 24 hours' },
  { id: '7d', label: 'Last 7 days' },
];

export function CommanderPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const teams = useQuery<{ teams: Team[] }>({
    queryKey: ['teams'],
    queryFn: () => api<{ teams: Team[] }>('/api/teams'),
  });
  const teamId = searchParams.get('team_id') || teams.data?.teams?.[0]?.id || '';
  const setTeamId = (id: string) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('team_id', id); else next.delete('team_id');
    setSearchParams(next, { replace: true });
  };
  const windowId = searchParams.get('window') || '8h';
  const setWindow = (w: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('window', w);
    setSearchParams(next, { replace: true });
  };

  const snapshot = useQuery<CommanderSnapshot>({
    queryKey: ['commander-snapshot', teamId, windowId],
    queryFn: () => api<CommanderSnapshot>(`/api/admin/commander/snapshot?team_id=${encodeURIComponent(teamId)}&window=${windowId}`),
    enabled: !!teamId,
    refetchInterval: 30_000,
  });

  const summary = useMemo(() => snapshot.data?.totals, [snapshot.data]);

  return (
    <div className="space-y-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/admin" className="text-sm text-ink-muted hover:text-ink">← Admin</Link>
          <h1 className="mt-1 text-2xl font-semibold">Commander</h1>
          <p className="text-ink-muted">
            Who's working in the last{' '}
            <strong className="text-ink">{WINDOW_OPTIONS.find((w) => w.id === windowId)?.label.toLowerCase() ?? windowId}</strong>.
            Counters count <em>scored</em> events only — anything that credited XP or coins. Most-recent move first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label htmlFor="commander-team" className="text-xs text-ink-muted">Team</label>
          <select
            id="commander-team"
            className="rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm"
            value={teamId}
            onChange={(e) => setTeamId(e.target.value)}
          >
            {(teams.data?.teams ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
          <label htmlFor="commander-window" className="ml-2 text-xs text-ink-muted">Window</label>
          <select
            id="commander-window"
            className="rounded-md border border-border bg-bg-elevated px-2 py-1 text-sm"
            value={windowId}
            onChange={(e) => setWindow(e.target.value)}
          >
            {WINDOW_OPTIONS.map((w) => (
              <option key={w.id} value={w.id}>{w.label}</option>
            ))}
          </select>
        </div>
      </header>

      {/* One-line team totals. Lets the operator glance the
          Commander without scanning every card. Counts scoped
          to scored events in the window — exactly what the
          per-user counters count too, so they sum. */}
      {summary && (
        <div className="card flex flex-wrap gap-x-6 gap-y-2 text-sm text-ink-muted">
          <span>
            <strong className="text-ink tabular-nums">{summary.events}</strong> events
          </span>
          <span>
            <strong className="text-ink tabular-nums">{summary.tickets_closed}</strong> tickets closed
          </span>
          <span>
            <strong className="text-ink tabular-nums">{summary.prs_opened}</strong> PRs opened
          </span>
          <span>
            <strong className="text-ink tabular-nums">{summary.prs_merged}</strong> PRs merged
          </span>
        </div>
      )}

      {snapshot.isLoading ? (
        <p className="text-ink-muted">Loading…</p>
      ) : snapshot.isError ? (
        <p className="text-accent-danger">
          Failed to load: {snapshot.error instanceof ApiError ? snapshot.error.message : String(snapshot.error)}
        </p>
      ) : snapshot.data ? (
        <CommanderView data={snapshot.data} />
      ) : null}
    </div>
  );
}

function CommanderView({ data }: { data: CommanderSnapshot }) {
  if (data.contributors.length === 0) {
    return (
      <div className="card">
        <p className="text-sm text-ink-muted">No team members on this team.</p>
      </div>
    );
  }

  // Three groups: still shipping, drifted (did work, then went quiet), and
  // silent (nothing at all). Drifted and silent share the Standing by band;
  // the drifted are listed first, so the operator sees who USED to ship.
  // THREE GROUPS, EACH PERSON IN EXACTLY ONE - see commander-groups.ts, which is
  // where the split lives and where it is tested. `drifted` used to be a SUBSET
  // of `active` that was ALSO rendered under Standing by, so anyone who had gone
  // quiet appeared twice. Same person, not two, which is how it was reported.
  const groups = partitionContributors(data.contributors, data.window_hours);
  const { drifted, current, silent: standing } = groups;

  // Phase 3: hero strips.
  //  • On fire = top 3 by event count among those STILL shipping. It read from
  //    the whole window before, so a contributor who went quiet hours ago could
  //    be badged "On fire now".
  const onFire = current.slice(0, 3);

  return (
    <div className="space-y-6">
      {onFire.length > 0 && (
        // Three chips in a row. Each shows the handle + a
        // mini bar of damage dealt vs their own counter
        // weight (events in window). Tasteful, not loud.
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">
            On fire now
          </h2>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            {onFire.map((c) => <OnFireChip key={c.user_id} c={c} />)}
          </div>
        </section>
      )}

      {current.length > 0 ? (
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">
            Active ({current.length})
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {current.map((c) => <ContributorCard key={c.user_id} c={c} />)}
          </div>
        </section>
      ) : (
        <div className="card">
          <p className="text-sm text-ink-muted">No scored events in this window. Try a longer window.</p>
        </div>
      )}

      {/* Standing by: the long-silent are surfaced first so
          the operator sees them before the truly inactive.
          Two sub-bands: 'drifted' (had events but quiet for
          half the window) and 'silent' (zero events). */}
      {(drifted.length > 0 || standing.length > 0) && (
        <section>
          <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-ink-muted">
            Standing by ({drifted.length + standing.length})
          </h2>
          {drifted.length > 0 && (
            <div className="mb-3 grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {drifted.map((c) => <ContributorCard key={c.user_id} c={c} dimmed />)}
            </div>
          )}
          {standing.length > 0 && (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
              {standing.map((c) => <ContributorCard key={c.user_id} c={c} dimmed />)}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

// On-fire chip: short, dense — exactly handle + count +
// last seen. Stays compact so the row reads as a quick
// leaderboard rather than another card.
function OnFireChip({ c }: { c: Contributor }) {
  return (
    <div className="card flex items-center gap-3 px-3 py-2">
      <span
        className="h-6 w-6 shrink-0 rounded-full ring-1 ring-black/40"
        style={{ backgroundColor: c.color ?? '#64748b' }}
        aria-hidden
      />
      <div className="min-w-0 flex-1 flex items-baseline gap-2">
        <span className="truncate text-sm font-semibold">{c.name ?? `@${c.handle}`}</span>
        <span className="ml-auto font-mono text-sm tabular-nums text-amber-300">{c.counters.events}</span>
        <span className="text-xs text-ink-muted">events</span>
      </div>
    </div>
  );
}

function ContributorCard({ c, dimmed = false }: { c: Contributor; dimmed?: boolean }) {
  const lastSeen = c.last_seen_at ? formatRelative(c.last_seen_at) : 'no events';
  // Phase 3: 'freshness dot' next to the handle. Green when
  // seen in the last hour, amber when seen inside the
  // window's first half, dim/empty otherwise. v0.8.9 polish.
  const freshness = c.last_seen_at ? freshnessLevel(c.last_seen_at) : 'silent';
  return (
    <div className={`card space-y-3 ${dimmed ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-3">
        <span
          className="h-8 w-8 shrink-0 rounded-full ring-1 ring-black/40"
          style={{ backgroundColor: c.color ?? '#64748b' }}
          aria-hidden
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className={`h-2 w-2 shrink-0 rounded-full ${freshnessDot(freshness)}`} aria-hidden title={freshness} />
            <span className="truncate font-semibold">{c.name ?? `@${c.handle}`}</span>
          </div>
          <div className="text-xs text-ink-muted">last seen: {lastSeen}</div>
        </div>
      </div>
      {/* Counter row: events / closed / PRs. Same numbers the
          totals line shows, just scoped to this contributor. */}
      <div className="grid grid-cols-3 gap-2 text-center text-sm">
        <Counter n={c.counters.events} label="events" />
        <Counter n={c.counters.tickets_closed} label="closed" highlight />
        <Counter n={c.counters.prs_merged} label="merged" />
      </div>
      {/* Phase 2 chips: boss-fight involvement + side-quest
          tally. Boss chips show this user's damage + that
          boss's HP. Side-quest is a single amber pip — only
          surfaced when > 0. */}
      {(c.boss_fights.length > 0 || c.side_quests > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {c.boss_fights.map((b) => (
            <span
              key={b.id}
              className="inline-flex items-center gap-1.5 rounded border border-red-400/40 bg-red-950/30 px-2 py-0.5 text-[11px] font-medium text-red-100"
              title={`Dealt ${b.damage} of ${b.hp} HP on this boss`}
            >
              <span aria-hidden>👹</span>
              <span className="truncate max-w-[12rem]">{b.name}</span>
              <span className="font-mono tabular-nums text-red-200">{b.damage}</span>
            </span>
          ))}
          {c.side_quests > 0 && (
            <span
              className="inline-flex items-center gap-1.5 rounded border border-amber-400/40 bg-amber-950/30 px-2 py-0.5 text-[11px] font-medium text-amber-100"
              title="Tickets closed in this window that weren't part of any active adventure's commitment"
            >
              <span aria-hidden>🗡</span>
              <span className="font-mono tabular-nums">{c.side_quests}</span>
              <span>side quest{c.side_quests === 1 ? '' : 's'}</span>
            </span>
          )}
        </div>
      )}
      {c.recent.length > 0 && (
        <ul className="space-y-1 border-t border-border pt-2 text-xs">
          {c.recent.map((r) => (
            <li key={r.event_id} className="flex items-baseline gap-2">
              <span className="font-mono text-[10px] text-ink-muted whitespace-nowrap">
                {new Date(r.occurred_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </span>
              <span className="truncate text-ink" title={r.description}>{r.description}</span>
              <span className="ml-auto shrink-0 rounded bg-bg-base px-1.5 py-0.5 text-[10px] text-ink-muted">
                {eventLabel(r.type)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Counter({ n, label, highlight }: { n: number; label: string; highlight?: boolean }) {
  return (
    <div className={`rounded bg-bg-base p-2 ${highlight && n > 0 ? 'ring-1 ring-amber-300/40' : ''}`}>
      <div className="text-lg font-bold tabular-nums text-ink">{n}</div>
      <div className="text-[10px] uppercase tracking-wide text-ink-muted">{label}</div>
    </div>
  );
}

// Short labels for the recent-moves list. Long type names
// (e.g. 'issue.transition') would crowd the chip.
function eventLabel(type: string): string {
  switch (type) {
    case 'issue.transition': return 'trans';
    case 'issue.commented': return 'comment';
    case 'issue.created': return 'created';
    case 'pr.opened': return 'pr open';
    case 'pr.review': return 'review';
    case 'pr.merged': return 'merge';
    case 'commit': return 'commit';
    case 'kudos.given': return 'kudos';
    default: return type;
  }
}

// Compact relative time. Always past (Commander never shows
// future events), so 'in …' is unnecessary. Falls back to the
// ISO string for >7 days.
function formatRelative(iso: string): string {
  const t = new Date(iso).getTime();
  const diff = Date.now() - t;
  if (diff < 60_000) return 'just now';
  const m = Math.floor(diff / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}

type Freshness = 'fresh' | 'recent' | 'stale' | 'silent';
// Phase 3: classify a contributor's last seen timestamp.
// fresh = within the last hour, recent = within the last 8h,
// stale = older than that. Drives the dot next to the handle.
function freshnessLevel(iso: string): Freshness {
  const age = Date.now() - new Date(iso).getTime();
  if (age < 60 * 60_000) return 'fresh';
  if (age < 8 * 60 * 60_000) return 'recent';
  return 'stale';
}
function freshnessDot(level: Freshness): string {
  switch (level) {
    case 'fresh': return 'bg-emerald-400 ring-2 ring-emerald-400/30';
    case 'recent': return 'bg-amber-400';
    case 'stale': return 'bg-bg-elevated';
    default: return 'bg-bg-elevated';
  }
}
